<script setup>
import { onMounted, ref } from "vue";
import { gql } from "@apollo/client/core";
import apolloClient from "@/apollo";
import MiniStatisticsCard from "@/examples/Cards/MiniStatisticsCard.vue";
import GradientLineChart from "@/examples/Charts/GradientLineChart.vue";
import CategoriesList from "./components/CategoriesList.vue";

// GraphQL Queries
const GET_ALL_RESERVATIONS = gql`
  query GetAllReservations {
    getAllReservations {
      status
      message
      data {
        id
        arriveDate
        departureDate
        accomodation {
          id
          name
          region
        }
        client {
          firstName
          id
        }
        docReservation {
          id
          docUrl
          createdAt
        }
      }
    }
  }
`;

const GET_RESERVATION_STATS = gql`
  query GetReservationStats {
    getReservationStats {
    totalConfirmed
    guesthouseConfirmedCount
    excursionCircuitsConfirmedCount
    campingConfirmedCount
    totalPending
    guesthousePendingCount
    excursionCircuitsPendingCount
    campingPendingCount
    totalConfirmedAmount
    }
  }
`;

const GET_RESERVATIONS_LAST_6_MONTHS = gql`
  query GetReservationsLast6Months {
    getReservationsLast6Months {
      month
      count
    }
  }
`;

const GET_RESERVATIONS_BY_REGION_TODAY = gql`
  query GetReservationsByRegionToday {
    getReservationsByRegionToday {
      region
      count
    }
  }
`;

const GET_ALL_CLIENTS = gql`
  query GetAllClients {
    getAllClients {
      status
      message
      data {
        firstName
        lastName
        notification {
          id
          message
          subject
          status
        }
      }
    }
  }
`;

const GET_ACCOMODATIONS_STATS = gql`
  query GetAccomodationsStats {
    getAccomodationsStats {
      status
      message
      data {
        totalAccomodations
        guesthouseAccomodationCount
        excursionCircuitsAccomodationCount
        campingAccomodationCount
      }
    }
  }
`;

// Reactive data
const totalReservations = ref(0);
const currentMonthReservations = ref(0);
const guesthouseConfirmedCount = ref(0);
const excursionCircuitsConfirmedCount = ref(0);
const campingConfirmedCount = ref(0);
const totalConfirmedAmount = ref(0);
const totalPending = ref(0);
const guesthousePendingCount = ref(0);
const excursionCircuitsPendingCount = ref(0);
const campingPendingCount = ref(0);
const chartLabels = ref([]);
const chartData = ref([]);
const reservationsByRegion = ref([]);
const totalClients = ref(0);
const totalAccomodations = ref(0);
const guesthouseAccomodationCount = ref(0);
const excursionCircuitsAccomodationCount = ref(0);
const campingAccomodationCount = ref(0);

// Fetch reservations
const fetchReservations = async () => {
  try {
    const { data } = await apolloClient.query({
      query: GET_ALL_RESERVATIONS,
      fetchPolicy: 'no-cache',
    });
    if (data?.getAllReservations?.data) {
      const reservations = data.getAllReservations.data;
      totalReservations.value = reservations.length;

      // Filter reservations for current month
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      currentMonthReservations.value = reservations.filter(res => {
        const arriveDate = new Date(res.arriveDate);
        return arriveDate.getMonth() === currentMonth && arriveDate.getFullYear() === currentYear;
      }).length;
    }
  } catch (error) {
    console.error("Error fetching reservations:", error);
  }
};

onMounted(() => {
  fetchReservations();
  fetchReservationStats();
  fetchReservationsLast6Months();
  fetchReservationsByRegionToday();
  fetchAllClients();
  fetchAccomodationsStats();
});

// Fetch reservation stats
const fetchReservationStats = async () => {
  try {
    const { data } = await apolloClient.query({
      query: GET_RESERVATION_STATS,
      fetchPolicy: 'no-cache',
    });
    if (data?.getReservationStats) {
      guesthouseConfirmedCount.value = data.getReservationStats.guesthouseConfirmedCount;
      excursionCircuitsConfirmedCount.value = data.getReservationStats.excursionCircuitsConfirmedCount;
      campingConfirmedCount.value = data.getReservationStats.campingConfirmedCount;
      totalConfirmedAmount.value = data.getReservationStats.totalConfirmedAmount;
      totalPending.value = data.getReservationStats.totalPending;
      guesthousePendingCount.value = data.getReservationStats.guesthousePendingCount;
      excursionCircuitsPendingCount.value = data.getReservationStats.excursionCircuitsPendingCount;
      campingPendingCount.value = data.getReservationStats.campingPendingCount;
    }
  } catch (error) {
    console.error("Error fetching reservation stats:", error);
  }
};

// Fetch reservations last 6 months
const fetchReservationsLast6Months = async () => {
  try {
    const { data } = await apolloClient.query({
      query: GET_RESERVATIONS_LAST_6_MONTHS,
      fetchPolicy: 'no-cache',
    });
    if (data?.getReservationsLast6Months) {
      const monthsData = data.getReservationsLast6Months;
      chartLabels.value = monthsData.map(item => item.month);
      chartData.value = monthsData.map(item => item.count);
    }
  } catch (error) {
    console.error("Error fetching reservations last 6 months:", error);
  }
};

// Fetch reservations by region today
const fetchReservationsByRegionToday = async () => {
  try {
    const { data } = await apolloClient.query({
      query: GET_RESERVATIONS_BY_REGION_TODAY,
      fetchPolicy: 'no-cache',
    });
    if (data?.getReservationsByRegionToday) {
      reservationsByRegion.value = data.getReservationsByRegionToday;
    }
  } catch (error) {
    console.error("Error fetching reservations by region today:", error);
  }
};

// Fetch all clients
const fetchAllClients = async () => {
  try {
    const { data } = await apolloClient.query({
      query: GET_ALL_CLIENTS,
      fetchPolicy: 'no-cache',
    });
    if (data?.getAllClients?.data) {
      totalClients.value = data.getAllClients.data.length;
    }
  } catch (error) {
    console.error("Error fetching all clients:", error);
  }
};

// Fetch accomodations stats
const fetchAccomodationsStats = async () => {
  try {
    const { data } = await apolloClient.query({
      query: GET_ACCOMODATIONS_STATS,
      fetchPolicy: 'no-cache',
    });
    if (data?.getAccomodationsStats?.data) {
      totalAccomodations.value = data.getAccomodationsStats.data.totalAccomodations;
      guesthouseAccomodationCount.value = data.getAccomodationsStats.data.guesthouseAccomodationCount;
      excursionCircuitsAccomodationCount.value = data.getAccomodationsStats.data.excursionCircuitsAccomodationCount;
      campingAccomodationCount.value = data.getAccomodationsStats.data.campingAccomodationCount;
    }
  } catch (error) {
    console.error("Error fetching accomodations stats:", error);
  }
};

// No FR flag available, will use text only
</script>
<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-lg-12">
        <div class="row">
          <div class="col-lg-3 col-md-6 col-12">
            <mini-statistics-card
              title="Réservations Totales"
              :value="totalReservations.toString()"
              :description="`<span class='text-sm font-weight-bolder text-success'>${totalReservations.value > 0 ? Math.round((currentMonthReservations.value / totalReservations.value) * 100) : 0}%</span> ce mois`"
              :icon="{
                component: 'ni ni-calendar-grid-58',
                background: 'bg-gradient-primary',
                shape: 'rounded-circle',
              }"
            />
          </div>
          <div class="col-lg-3 col-md-6 col-12">
            <mini-statistics-card
              title="Maisons d'hôtes"
              :value="guesthouseConfirmedCount.toString()"
                            :description="`<span class='text-sm font-weight-bolder text-success'>+${guesthouseConfirmedCount.toString()}%</span> réservations`"
              :icon="{
                component: 'ni ni-building',
                background: 'bg-gradient-success',
                shape: 'rounded-circle',
              }"
            />
          </div>
          <div class="col-lg-3 col-md-6 col-12">
            <mini-statistics-card
              title="Campements"
              :value="campingConfirmedCount.toString()"
              :description="`<span class='text-sm font-weight-bolder text-success'>+${campingConfirmedCount.toString()}%</span> réservations`"
              :icon="{
                component: 'ni ni-map-big',
                background: 'bg-gradient-warning',
                shape: 'rounded-circle',
              }"
            />
          </div>
          <div class="col-lg-3 col-md-6 col-12">
            <mini-statistics-card
              title="Circuits d'excursion"
              :value="excursionCircuitsConfirmedCount.toString()"
              :description="`<span class='text-sm font-weight-bolder text-success'>+${excursionCircuitsConfirmedCount.toString()}%</span> réservations`"
              :icon="{
                component: 'ni ni-world',
                background: 'bg-gradient-secondary',
                shape: 'rounded-circle',
              }"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12 mb-lg">
            <!-- line chart -->
            <div class="card z-index-2">
              <!--
description="<i class='fa fa-arrow-up text-success'></i>
<span class='font-weight-bold'>12% d'augmentation</span> ce trimestre"
-->

              <gradient-line-chart
                id="chart-line"
                title="Évolution des Réservations"


                :chart="{
                  labels: chartLabels,
                  datasets: [
                    {
                      label: 'Réservations',
                      data: chartData,
                    },
                  ],
                }"
              />
            </div>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-lg-7 mb-lg-0 mb-4">
            <div class="card">
              <div class="p-3 pb-0 card-header">
                <div class="d-flex justify-content-between">
                  <h6 class="mb-2">Réservations par Région</h6>
                </div>
              </div>
              <div class="table-responsive">
                <table class="table align-items-center">
                  <tbody>
                    <tr v-for="(reservation, index) in reservationsByRegion" :key="index">
                      <td class="w-30">
                        <div class="px-2 py-1">
                          <p class="mb-0 text-xs font-weight-bold">
                            Région:
                          </p>
                          <h6 class="mb-0 text-sm">{{ reservation.region }}</h6>
                        </div>
                      </td>
                      <td>
                        <div class="text-center">
                          <p class="mb-0 text-xs font-weight-bold">Réservations:</p>
                          <h6 class="mb-0 text-sm">{{ reservation.count }}</h6>
                        </div>
                      </td>
                      <td>
                        <div class="text-center">
                          <p class="mb-0 text-xs font-weight-bold">Revenus:</p>
                          <h6 class="mb-0 text-sm">0</h6>
                        </div>
                      </td>
                      <td class="text-sm align-middle">
                        <div class="text-center col">
                          <p class="mb-0 text-xs font-weight-bold">Occupation:</p>
                          <h6 class="mb-0 text-sm">0</h6>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="col-lg-5">
            <categories-list
              :categories="[
                {
                  icon: {
                    component: 'ni ni-building',
                    background: 'primary',
                  },
                  label: 'Maisons d\'hôtes',
                  description: `${(guesthouseAccomodationCount - guesthouseConfirmedCount).toString()} disponibles <strong>${guesthouseConfirmedCount.toString()} réservées</strong>`,
                },
                {
                  icon: {
                    component: 'ni ni-world',
                    background: 'secondary',
                  },
                  label: 'Circuits d\'excursion',
                  description: `${(excursionCircuitsAccomodationCount - excursionCircuitsConfirmedCount).toString()} disponibles <strong>${excursionCircuitsConfirmedCount.toString()} réservés</strong>`,
                },
                {
                  icon: {
                    component: 'ni ni-map-big',
                    background: 'success',
                  },
                  label: 'Campements',
                  description: `${(campingAccomodationCount - campingConfirmedCount).toString()} emplacements <strong>${campingConfirmedCount.toString()} réservés</strong>`,
                },
                {
                  icon: { component: 'ni ni-calendar-grid-58', background: 'warning' },
                  label: 'Réservations en attente',
                  description: `${totalPending.toString()} réservations en attente`,
                },
                {
                  icon: { component: 'ni ni-satisfied', background: 'info' },
                  label: 'Clients',
                  description: totalClients.toString(),
                },
              ]"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

